import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import ClassBookDiaryPage from '../components/ClassBookDiaryPage';
import ClassBookSignaturesModal from '../components/ClassBookSignaturesModal';
import ClassBookStudentTallyModal from '../components/ClassBookStudentTallyModal';
import ClassBookDateNav from '../components/ClassBookDateNav';
import ClassBookDatePickerModal from '../components/ClassBookDatePickerModal';
import NewClassLocationModal from '../components/NewClassLocationModal';
import { getApiUrl } from '../utils/apiConfig';
import { isoDateToLongDisplay, localTodayIsoDate } from '../utils/dateFormat';
import { fetchVisibleStudents } from '../utils/fetchVisibleStudents';
import { getSupabaseClient } from '../utils/supabase';
import {
  listStudentSignatureUrlsByKey,
  signatureStorageKeyForStudentName,
} from '../utils/studentSignatures';
import {
  classDateIsoToLocalDate,
  createTrainingClassLocation,
  fetchClassBookSession,
  fetchTrainingClassLocations,
  locationDisplayName,
  setClassBookNoClass,
  syncClassBookAttendance,
  recordClassAttendancePayment,
  recordClassExtraContribution,
  shiftClassDateIso,
  updateTrainingClassLocation,
} from '../utils/classBookApi';

const LIGHT_CREAM = '#faf8f4';

function studentDisplayName(student) {
  return String(student?.student_name || student?.username || '').trim();
}

function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export default function ClassBookPage({ navigation, route }) {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [attendedStudents, setAttendedStudents] = useState([]);
  const [attendanceLines, setAttendanceLines] = useState([]);
  const [feeRate, setFeeRate] = useState(null);
  const [loadingClass, setLoadingClass] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [showNewLocationModal, setShowNewLocationModal] = useState(false);
  const [savingLinePayment, setSavingLinePayment] = useState(false);
  const [savingLineExtra, setSavingLineExtra] = useState(false);
  const [selectedDateIso, setSelectedDateIso] = useState(() => localTodayIsoDate());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAddPicklistInput, setShowAddPicklistInput] = useState(false);
  const [newPicklistName, setNewPicklistName] = useState('');
  const [addingPicklistName, setAddingPicklistName] = useState(false);
  const [showVisitorsInDropdown, setShowVisitorsInDropdown] = useState(false);
  const [visibleStudents, setVisibleStudents] = useState([]);
  const [noClass, setNoClass] = useState(false);
  const [savingNoClass, setSavingNoClass] = useState(false);
  const [showSignaturesModal, setShowSignaturesModal] = useState(false);
  const [showStudentTallyDropdown, setShowStudentTallyDropdown] = useState(false);
  const [showStudentTallyModal, setShowStudentTallyModal] = useState(false);
  const [tallyStudentName, setTallyStudentName] = useState('');
  const [signatureUrlByKey, setSignatureUrlByKey] = useState({});
  const classDate = selectedDateIso;
  const diaryDate = useMemo(() => classDateIsoToLocalDate(selectedDateIso), [selectedDateIso]);

  useEffect(() => {
    const uid = route?.params?.userId || currentUser?.id || 1;
    if (route?.params?.user) {
      setCurrentUser(route.params.user);
      return;
    }
    fetch(`${getApiUrl()}/api/users/${uid}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCurrentUser(data))
      .catch(() => {});
  }, [route?.params?.user, route?.params?.userId, currentUser?.id]);

  const loadLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const rows = await fetchTrainingClassLocations();
      setLocations(rows);
    } catch (error) {
      console.error('Error loading class locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const selectedLocation = useMemo(
    () => locations.find((loc) => loc.id === selectedLocationId) ?? null,
    [locations, selectedLocationId]
  );

  const locationStudentNames = useMemo(() => {
    const raw = selectedLocation?.students;
    if (!Array.isArray(raw)) return [];
    return raw.map((name) => String(name || '').trim()).filter(Boolean);
  }, [selectedLocation]);

  useEffect(() => {
    setShowStudentTallyDropdown(false);
    setShowStudentTallyModal(false);
    setTallyStudentName('');
  }, [selectedLocationId]);

  useEffect(() => {
    if (selectedLocationId == null) {
      setVisibleStudents([]);
      return;
    }
    void (async () => {
      try {
        const { students } = await fetchVisibleStudents({ requester: currentUser });
        setVisibleStudents(Array.isArray(students) ? students : []);
      } catch {
        setVisibleStudents([]);
      }
    })();
  }, [selectedLocationId, currentUser?.id]);

  const visitorStudents = useMemo(() => {
    const seen = new Set();
    return visibleStudents
      .map((student) => ({
        id: student.id,
        name: studentDisplayName(student),
      }))
      .filter((item) => {
        if (!item.name) return false;
        if (locationStudentNames.some((locName) => namesMatch(locName, item.name))) return false;
        const key = item.name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleStudents, locationStudentNames]);

  const attendanceTally = attendedStudents.length;

  const applySession = useCallback((session) => {
    const names = Array.isArray(session?.attendance)
      ? session.attendance.map((line) => String(line.student_name || '').trim()).filter(Boolean)
      : Array.isArray(session?.class?.students_attended)
        ? session.class.students_attended
        : [];
    setAttendedStudents(names);
    setAttendanceLines(Array.isArray(session?.attendance) ? session.attendance : []);
    setFeeRate(session?.fee_rate ?? null);
    setNoClass(session?.class?.no_class === true);
  }, []);

  const loadSignatureUrls = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setSignatureUrlByKey({});
      return;
    }
    try {
      const map = await listStudentSignatureUrlsByKey(supabase);
      setSignatureUrlByKey(map);
    } catch (error) {
      console.error('Error loading student signatures:', error);
      setSignatureUrlByKey({});
    }
  }, []);

  useEffect(() => {
    void loadSignatureUrls();
  }, [loadSignatureUrls]);

  const loadClassForDate = useCallback(async (locationId, dateIso) => {
    if (locationId == null) {
      setAttendedStudents([]);
      setAttendanceLines([]);
      setFeeRate(null);
      setNoClass(false);
      return;
    }
    setLoadingClass(true);
    setAttendanceError('');
    try {
      const session = await fetchClassBookSession(locationId, dateIso);
      applySession(session);
    } catch (error) {
      console.error('Error loading class record:', error);
      setAttendedStudents([]);
      setAttendanceLines([]);
      setFeeRate(null);
      setNoClass(false);
      setAttendanceError('Could not load the class record for this date.');
    } finally {
      setLoadingClass(false);
    }
  }, [applySession]);

  useEffect(() => {
    if (selectedLocationId == null) return;
    void loadClassForDate(selectedLocationId, classDate);
  }, [selectedLocationId, classDate, loadClassForDate]);

  const handleLocationSelect = (location) => {
    setSelectedLocationId(location.id);
    setShowLocationDropdown(false);
    setShowStudentDropdown(false);
    setShowVisitorsInDropdown(false);
    setSelectedDateIso(localTodayIsoDate());
  };

  const handleDateSelect = (iso) => {
    setSelectedDateIso(iso);
    setShowStudentDropdown(false);
    setShowVisitorsInDropdown(false);
  };

  const handleShiftDate = useCallback((days) => {
    setSelectedDateIso((current) => shiftClassDateIso(current, days));
    setShowStudentDropdown(false);
    setShowVisitorsInDropdown(false);
  }, []);

  const persistAttendance = async (nextStudents) => {
    if (selectedLocationId == null) return;
    setSavingAttendance(true);
    setAttendanceError('');
    try {
      const session = await syncClassBookAttendance({
        locationId: selectedLocationId,
        classDate,
        studentNames: nextStudents,
      });
      applySession(session);
    } catch (error) {
      console.error('Error saving class attendance:', error);
      setAttendanceError(error?.message || 'Failed to save attendance.');
    } finally {
      setSavingAttendance(false);
    }
  };

  const handleSavePaymentToday = useCallback(
    async (attendanceId, studentName, amount, currency) => {
      if (selectedLocationId == null) return;
      setSavingLinePayment(true);
      setAttendanceError('');
      try {
        await recordClassAttendancePayment({
          locationId: selectedLocationId,
          studentName,
          attendanceId,
          amount,
          currency: currency || feeRate?.currency || 'EUR',
          paidAt: classDate,
        });
        await loadClassForDate(selectedLocationId, classDate);
      } catch (error) {
        setAttendanceError(error?.message || 'Failed to save payment.');
        throw error;
      } finally {
        setSavingLinePayment(false);
      }
    },
    [selectedLocationId, classDate, feeRate, loadClassForDate]
  );

  const handleSaveExtraContribution = useCallback(
    async (attendanceId, studentName, amount, currency, reason) => {
      if (selectedLocationId == null) return;
      setSavingLineExtra(true);
      setAttendanceError('');
      try {
        await recordClassExtraContribution({
          locationId: selectedLocationId,
          studentName,
          attendanceId,
          amount,
          currency: currency || feeRate?.currency || 'EUR',
          reason,
          paidAt: classDate,
        });
        await loadClassForDate(selectedLocationId, classDate);
      } catch (error) {
        setAttendanceError(error?.message || 'Failed to save extra contribution.');
        throw error;
      } finally {
        setSavingLineExtra(false);
      }
    },
    [selectedLocationId, classDate, feeRate, loadClassForDate]
  );

  const signatureModalStudents = useMemo(() => {
    const seen = new Set();
    return visibleStudents
      .filter((student) => {
        const name = studentDisplayName(student);
        if (!name) return false;
        const key = name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b)));
  }, [visibleStudents]);

  const tallyStudentOptions = useMemo(
    () => signatureModalStudents.map((student) => studentDisplayName(student)).filter(Boolean),
    [signatureModalStudents]
  );

  const handleSelectTallyStudent = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    setTallyStudentName(trimmed);
    setShowStudentTallyDropdown(false);
    setShowStudentTallyModal(true);
  };

  const handleToggleNoClass = async () => {
    if (selectedLocationId == null || savingNoClass) return;
    setSavingNoClass(true);
    setAttendanceError('');
    try {
      const session = await setClassBookNoClass({
        locationId: selectedLocationId,
        classDate,
        noClass: !noClass,
      });
      applySession(session);
    } catch (error) {
      setAttendanceError(error?.message || 'Failed to update no-class stamp.');
    } finally {
      setSavingNoClass(false);
    }
  };

  const handleSignatureSaved = ({ studentName, url }) => {
    const key = signatureStorageKeyForStudentName(studentName);
    setSignatureUrlByKey((current) => ({ ...current, [key]: url }));
  };

  const toggleStudentAttendance = (studentName) => {
    const name = String(studentName || '').trim();
    if (!name || savingAttendance) return;
    const next = attendedStudents.some((n) => namesMatch(n, name))
      ? attendedStudents.filter((n) => !namesMatch(n, name))
      : [...attendedStudents, name];
    void persistAttendance(next);
  };

  const handleAddPicklistName = async () => {
    const trimmed = newPicklistName.trim();
    if (!trimmed || selectedLocationId == null) return;
    if (locationStudentNames.some((name) => namesMatch(name, trimmed))) {
      setAttendanceError('That name is already on the picklist.');
      return;
    }
    setAddingPicklistName(true);
    setAttendanceError('');
    try {
      await updateTrainingClassLocation(selectedLocationId, {
        students: [...locationStudentNames, trimmed],
      });
      await loadLocations();
      setNewPicklistName('');
      setShowAddPicklistInput(false);
    } catch (error) {
      setAttendanceError(error?.message || 'Failed to add name to picklist.');
    } finally {
      setAddingPicklistName(false);
    }
  };

  const renderStudentPickRow = (name, keySuffix = '') => {
    const selected = attendedStudents.some((n) => namesMatch(n, name));
    return (
      <TouchableOpacity
        key={`${name}${keySuffix}`}
        style={styles.dropdownRow}
        onPress={() => toggleStudentAttendance(name)}
        disabled={savingAttendance}
        {...(Platform.OS === 'web' ? { dataSet: { classBookStudentRow: 'true' } } : {})}
      >
        <Text style={styles.studentCheck}>{selected ? '☑' : '☐'}</Text>
        <Text style={styles.dropdownRowText}>{name}</Text>
      </TouchableOpacity>
    );
  };

  const handleCreateLocation = async (payload) => {
    const created = await createTrainingClassLocation(payload);
    await loadLocations();
    if (created?.id != null) {
      setSelectedLocationId(created.id);
    }
  };

  const selectedLocationLabel = selectedLocation
    ? locationDisplayName(selectedLocation)
    : 'Select Training Class Location';

  const studentSummary =
    attendedStudents.length > 0 ? attendedStudents.join(', ') : 'Select students who attended';

  const selectedDateLabel = isoDateToLongDisplay(selectedDateIso, false);
  const isTodaySelected = selectedDateIso === localTodayIsoDate();
  const studentsSectionLabel = isTodaySelected
    ? 'Students who attended today'
    : `Students who attended on ${selectedDateLabel}`;

  return (
    <View
      style={styles.container}
      data-screen="class-book"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Class Book</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={styles.title}>Class Book</Text>
        <Text style={styles.subtitle}>Record class attendance by location and day.</Text>

        <View style={styles.locationRow}>
          <View style={styles.locationDropdownWrap}>
            <Text style={styles.label}>Training Class Location</Text>
            <TouchableOpacity
              style={styles.dropdownTrigger}
              onPress={() => setShowLocationDropdown((open) => !open)}
              disabled={loadingLocations}
              {...(Platform.OS === 'web' ? { dataSet: { classBookLocationTrigger: 'true' } } : {})}
            >
              <Text
                style={[
                  styles.dropdownTriggerText,
                  !selectedLocation && styles.dropdownTriggerPlaceholder,
                ]}
                numberOfLines={2}
              >
                {loadingLocations ? 'Loading locations…' : selectedLocationLabel}
              </Text>
              <Text style={styles.dropdownArrow}>{showLocationDropdown ? '▲' : '▼'}</Text>
            </TouchableOpacity>

            {showLocationDropdown && !loadingLocations ? (
              <ScrollView
                style={styles.dropdownList}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                {...(Platform.OS === 'web' ? { dataSet: { classBookLocationList: 'true' } } : {})}
              >
                {locations.length === 0 ? (
                  <Text style={styles.emptyDropdownText}>No locations yet</Text>
                ) : (
                  locations.map((location) => (
                    <TouchableOpacity
                      key={location.id}
                      style={styles.dropdownRow}
                      onPress={() => handleLocationSelect(location)}
                      {...(Platform.OS === 'web' ? { dataSet: { classBookLocationRow: 'true' } } : {})}
                    >
                      <Text style={styles.dropdownRowText}>{locationDisplayName(location)}</Text>
                    </TouchableOpacity>
                  ))
                )}
              </ScrollView>
            ) : null}
          </View>

          <TouchableOpacity
            style={styles.addLocationBtn}
            onPress={() => setShowNewLocationModal(true)}
            accessibilityLabel="Add class location"
            {...(Platform.OS === 'web' ? { dataSet: { classBookAddLocationBtn: 'true' } } : {})}
          >
            <MaterialIcons name="add" size={28} color={theme.colors.primary || '#d4af37'} />
          </TouchableOpacity>
        </View>

        {selectedLocation ? (
          <>
            <View style={styles.datePickerRow}>
              <TouchableOpacity
                style={styles.calendarBtn}
                onPress={() => setShowDatePicker(true)}
                accessibilityLabel="Choose class date"
                {...(Platform.OS === 'web' ? { dataSet: { classBookCalendarBtn: 'true' } } : {})}
              >
                <MaterialIcons
                  name="calendar-today"
                  size={24}
                  color={theme.colors.primary || '#d4af37'}
                />
              </TouchableOpacity>
              <View style={styles.dateLabelWrap}>
                <Text style={styles.dateLabelTitle}>Diary date</Text>
                <Text style={styles.dateLabelValue}>{selectedDateLabel}</Text>
              </View>
            </View>

            <ClassBookDateNav
              onShiftDays={handleShiftDate}
              disabled={loadingClass || savingAttendance}
              iconColor={theme.colors.primary || '#d4af37'}
              buttonBackground={theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM}
              borderColor={theme.colors.border}
            />

            <View style={styles.studentPickerBlock}>
              <View style={styles.studentPickerHeader}>
                <Text style={styles.label}>{studentsSectionLabel}</Text>
                <Text style={styles.tallyText}>Total: {attendanceTally}</Text>
              </View>
              <TouchableOpacity
                style={styles.dropdownTrigger}
                onPress={() => {
                  setShowStudentDropdown((open) => !open);
                  if (showStudentDropdown) setShowVisitorsInDropdown(false);
                }}
                disabled={loadingClass || savingAttendance}
                {...(Platform.OS === 'web' ? { dataSet: { classBookStudentTrigger: 'true' } } : {})}
              >
                <Text
                  style={[
                    styles.dropdownTriggerText,
                    attendedStudents.length === 0 && styles.dropdownTriggerPlaceholder,
                  ]}
                  numberOfLines={2}
                >
                  {studentSummary}
                </Text>
                <Text style={styles.dropdownArrow}>{showStudentDropdown ? '▲' : '▼'}</Text>
              </TouchableOpacity>

              {showStudentDropdown ? (
                <ScrollView
                  style={styles.dropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  {...(Platform.OS === 'web' ? { dataSet: { classBookStudentList: 'true' } } : {})}
                >
                  {locationStudentNames.length === 0 ? (
                    <Text style={styles.emptyDropdownText}>
                      No students on this location picklist yet
                    </Text>
                  ) : (
                    locationStudentNames.map((name) => renderStudentPickRow(name))
                  )}

                  <View style={styles.dropdownDivider} />

                  <TouchableOpacity
                    style={[styles.dropdownRow, styles.visitorsMenuRow]}
                    onPress={() => setShowVisitorsInDropdown((open) => !open)}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookVisitorsRow: 'true' } } : {})}
                  >
                    <Text style={styles.visitorsMenuText}>Visitors</Text>
                    <Text style={styles.dropdownArrow}>{showVisitorsInDropdown ? '▲' : '▼'}</Text>
                  </TouchableOpacity>

                  {showVisitorsInDropdown ? (
                    visitorStudents.length === 0 ? (
                      <Text style={styles.emptyDropdownText}>No visiting students available</Text>
                    ) : (
                      visitorStudents.map((visitor) =>
                        renderStudentPickRow(visitor.name, `-visitor-${visitor.id}`)
                      )
                    )
                  ) : null}
                </ScrollView>
              ) : null}

              {loadingClass || savingAttendance ? (
                <ActivityIndicator
                  style={styles.inlineLoader}
                  color={theme.colors.primary || '#d4af37'}
                />
              ) : null}
              {attendanceError ? <Text style={styles.errorText}>{attendanceError}</Text> : null}
            </View>

            <ClassBookDiaryPage
              date={diaryDate}
              attendanceLines={attendanceLines}
              attendanceCount={attendanceTally}
              feeRate={feeRate}
              noClass={noClass}
              signatureUrlByKey={signatureUrlByKey}
              onSavePaymentToday={handleSavePaymentToday}
              onSaveExtraContribution={handleSaveExtraContribution}
              savingLinePayment={savingLinePayment}
              savingLineExtra={savingLineExtra}
            />

            <View style={styles.addNameSection}>
              <View style={styles.addNameToolbar}>
                <TouchableOpacity
                  style={[styles.noClassBtn, (savingNoClass || loadingClass) && styles.noClassBtnDisabled]}
                  onPress={() => void handleToggleNoClass()}
                  disabled={savingNoClass || loadingClass}
                  accessibilityLabel={noClass ? 'Remove no class stamp' : 'Stamp no class'}
                  {...(Platform.OS === 'web' ? { dataSet: { classBookNoClassBtn: 'true' } } : {})}
                >
                  {savingNoClass ? (
                    <ActivityIndicator size="small" color="#111" />
                  ) : (
                    <Text style={styles.noClassBtnText}>No Class</Text>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.addNameBtn}
                  onPress={() => setShowAddPicklistInput((open) => !open)}
                  accessibilityLabel="Add picklist name"
                  {...(Platform.OS === 'web' ? { dataSet: { classBookAddPicklistBtn: 'true' } } : {})}
                >
                  <MaterialIcons name="add" size={28} color={theme.colors.primary || '#d4af37'} />
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.signaturesBtn}
                  onPress={() => setShowSignaturesModal(true)}
                  accessibilityLabel="Manage student signatures"
                  {...(Platform.OS === 'web' ? { dataSet: { classBookSignaturesBtn: 'true' } } : {})}
                >
                  <MaterialIcons name="edit" size={24} color={theme.colors.primary || '#d4af37'} />
                </TouchableOpacity>
              </View>

              <View
                style={styles.studentTallyRow}
                {...(Platform.OS === 'web' ? { dataSet: { classBookStudentTallyRow: 'true' } } : {})}
              >
                <Text style={styles.studentTallyLabel}>Student Tally</Text>
                <View style={[styles.studentTallyDropdownWrap, { zIndex: 900 }]}>
                  <TouchableOpacity
                    style={styles.studentTallyTrigger}
                    onPress={() => setShowStudentTallyDropdown((open) => !open)}
                    disabled={!selectedLocationId || tallyStudentOptions.length === 0}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookStudentTallyTrigger: 'true' } } : {})}
                  >
                    <Text
                      style={[
                        styles.studentTallyTriggerText,
                        !tallyStudentName && styles.dropdownTriggerPlaceholder,
                      ]}
                      numberOfLines={1}
                    >
                      {tallyStudentName || 'Select student'}
                    </Text>
                    <Text style={styles.dropdownArrow}>
                      {showStudentTallyDropdown ? '▲' : '▼'}
                    </Text>
                  </TouchableOpacity>

                  {showStudentTallyDropdown ? (
                    <View
                      style={styles.studentTallyMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { classBookStudentTallyMenu: 'true' } } : {})}
                    >
                      <ScrollView style={styles.studentTallyMenuScroll} nestedScrollEnabled>
                        {tallyStudentOptions.length === 0 ? (
                          <Text style={styles.emptyDropdownText}>No students available</Text>
                        ) : (
                          tallyStudentOptions.map((name) => (
                            <TouchableOpacity
                              key={name}
                              style={styles.dropdownRow}
                              onPress={() => handleSelectTallyStudent(name)}
                              {...(Platform.OS === 'web' ? { dataSet: { classBookStudentTallyOption: 'true' } } : {})}
                            >
                              <Text style={styles.dropdownRowText}>{name}</Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </ScrollView>
                    </View>
                  ) : null}
                </View>
              </View>

              {showAddPicklistInput ? (
                <View
                  style={styles.addNameInputRow}
                  {...(Platform.OS === 'web' ? { dataSet: { classBookAddPicklistForm: 'true' } } : {})}
                >
                  <TextInput
                    style={styles.addNameInput}
                    value={newPicklistName}
                    onChangeText={setNewPicklistName}
                    placeholder="New picklist name"
                    placeholderTextColor={
                      theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                    }
                    editable={!addingPicklistName}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookAddPicklistInput: 'true' } } : {})}
                  />
                  <TouchableOpacity
                    style={[styles.addNameSaveBtn, addingPicklistName && styles.addNameSaveBtnDisabled]}
                    onPress={() => void handleAddPicklistName()}
                    disabled={addingPicklistName || !newPicklistName.trim()}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookAddPicklistSave: 'true' } } : {})}
                  >
                    <Text style={styles.addNameSaveBtnText}>
                      {addingPicklistName ? 'Adding…' : 'Add'}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          </>
        ) : null}
      </ScrollView>

      <ClassBookDatePickerModal
        visible={showDatePicker}
        selectedIso={selectedDateIso}
        onClose={() => setShowDatePicker(false)}
        onSelectDate={handleDateSelect}
      />

      <NewClassLocationModal
        visible={showNewLocationModal}
        onClose={() => setShowNewLocationModal(false)}
        onCreated={handleCreateLocation}
        currentUser={currentUser}
      />

      <ClassBookSignaturesModal
        visible={showSignaturesModal}
        onClose={() => setShowSignaturesModal(false)}
        students={signatureModalStudents}
        onSaved={handleSignatureSaved}
      />

      <ClassBookStudentTallyModal
        visible={showStudentTallyModal}
        onClose={() => setShowStudentTallyModal(false)}
        locationId={selectedLocationId}
        studentName={tallyStudentName}
        classDate={classDate}
        locationLabel={selectedLocation ? locationDisplayName(selectedLocation) : ''}
      />

      <Stopwatch />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    hamburgerContainer: {
      width: 40,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      width: 40,
      alignItems: 'flex-end',
      backgroundColor: 'transparent',
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
    },
    title: {
      fontSize: 28,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.primary || '#d4af37',
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 15,
      color: theme.colors.text,
      opacity: 0.8,
      marginBottom: 20,
      backgroundColor: 'transparent',
    },
    locationRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: 10,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    locationDropdownWrap: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownTriggerPlaceholder: {
      opacity: 0.65,
    },
    dropdownArrow: {
      marginLeft: 8,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownList: {
      maxHeight: 220,
      marginTop: 4,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    dropdownRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownRowText: {
      flex: 1,
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    studentCheck: {
      width: 28,
      fontSize: 20,
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    emptyDropdownText: {
      padding: 12,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    addLocationBtn: {
      width: 44,
      height: 44,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
      marginBottom: 0,
    },
    datePickerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    calendarBtn: {
      width: 44,
      height: 44,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    dateLabelWrap: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    dateLabelTitle: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    dateLabelValue: {
      marginTop: 2,
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    studentPickerBlock: {
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    studentPickerHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    tallyText: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    dropdownDivider: {
      height: 1,
      backgroundColor: theme.colors.border,
      marginVertical: 4,
    },
    visitorsMenuRow: {
      justifyContent: 'space-between',
    },
    visitorsMenuText: {
      flex: 1,
      fontSize: 15,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    addNameSection: {
      marginTop: 16,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    addNameToolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      backgroundColor: 'transparent',
    },
    studentTallyRow: {
      width: '100%',
      maxWidth: 420,
      marginTop: 14,
      backgroundColor: 'transparent',
    },
    studentTallyLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    studentTallyDropdownWrap: {
      position: 'relative',
      backgroundColor: 'transparent',
    },
    studentTallyTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    studentTallyTriggerText: {
      flex: 1,
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    studentTallyMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 4,
      maxHeight: 220,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      zIndex: 901,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
        : {
            elevation: 6,
            shadowColor: '#000',
            shadowOpacity: 0.15,
            shadowRadius: 6,
            shadowOffset: { width: 0, height: 2 },
          }),
    },
    studentTallyMenuScroll: {
      maxHeight: 220,
    },
    noClassBtn: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: '#d4af37',
      minWidth: 96,
      alignItems: 'center',
      justifyContent: 'center',
    },
    noClassBtnDisabled: {
      opacity: 0.65,
    },
    noClassBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#111',
      backgroundColor: 'transparent',
    },
    signaturesBtn: {
      width: 48,
      height: 48,
      borderRadius: 24,
      borderWidth: 1,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    addNameBtn: {
      width: 48,
      height: 48,
      borderRadius: 24,
      borderWidth: 1,
      borderColor: theme.colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    addNameInputRow: {
      marginTop: 12,
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      backgroundColor: 'transparent',
    },
    addNameInput: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    addNameSaveBtn: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: '#d4af37',
    },
    addNameSaveBtnDisabled: {
      opacity: 0.65,
    },
    addNameSaveBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#111',
      backgroundColor: 'transparent',
    },
    inlineLoader: {
      marginTop: 8,
    },
    errorText: {
      marginTop: 8,
      color: theme.isDarkMode ? '#e57373' : '#c62828',
      backgroundColor: 'transparent',
    },
  });
}
